import {
  ClaimResponse as Claim,
  Extension,
  CodeableConcept,
  Identifier,
  Adjudication,
} from '@sentara-health/sentara-api-hooks-core';
import {
  getUniqueParts,
  medicalClaimsConstants,
  formatYYYYMMDDToMDY,
} from 'common';
import { extractISODateFromClaim, toISODateString } from './DateFormat';
import { GlobalContext } from 'context';
import { useContext, useMemo } from 'react';
import { saveAs } from 'file-saver';

type StatusType = 'Paid' | 'Pending' | 'Denied' | 'Approved' | 'In Review';

type ClaimTotals = {
  totalCharges: string;
  totalNotCovered: string;
  totalCovered: string;
  totalDeductible: string;
  copayPlusCoinsurance: number;
  totalPaidByPlan: string;
  patientResponsibility: string;
  datePaymentSentToProvider: string;
};

type MedicalClaimDataType = {
  id: string;
  index: number;
  amountOwed: string;
  practiceName: string;
  providerName: string;
  serviceDate: string;
  status: StatusType;
  typeOfCare: string;
  showPayBill: boolean;
  claimId: string;
  memberId: string;
  network: string;
  eobFlag: boolean;
};

type ServiceTotals = {
  serviceType: string;
  charged: number | null;
  paidByPlan: number | null;
  patientResponsibility: number | null;
  amountNotCovered: number | null;
  amountAllowed: number | null;
  deductible: number | null;
  copay: number | null;
  coinsurance: number | null;
  copayCoinsurance: number | null;
  notes: string;
  noteDisplay?: string;
};

const PROVIDER_NAME_URL = 'providerName';
const PRACTICE_NAME_URL = 'practiceName';
const PATIENT_RESPONSIBILITY_CODE = 'totalPatientResponsibility';

export const getNormalizedStatus = (raw: string): StatusType => {
  if (!raw) {
    return 'In Review';
  }
  const normalized = raw.trim().toLowerCase();
  const map: Record<string, StatusType> = {
    paid: 'Paid',
    denied: 'Denied',
    'in review': 'In Review',
    pending: 'Pending',
    approved: 'Approved',
  };
  return map[normalized] ?? 'In Review';
};

/**
 * Single source of truth for determining if payment button/section should be shown.

 * 
 * @param rawStatus - The raw claim status from the API (any casing)
 * @param featureFlag - Whether the PayYourSentaraBill feature is enabled
 * @returns true if payment option should be shown, false otherwise
 */
export const showPayButton = (
  rawStatus: string | undefined,
  featureFlag: boolean | undefined
): boolean => {
  if (!featureFlag) return false;
  if (!rawStatus) return false;
  
  const normalized = getNormalizedStatus(rawStatus);
  return normalized === 'Paid' || normalized === 'Denied';
};

/**
 * Gets the network display label based on the API's string value.
 * 
 * @param networkStatus - The network status from API ("True"/"true" or "False"/"false" as string)
 * @param inNetworkLabel - The translation/label to show for in-network
 * @param outOfNetworkLabel - The translation/label to show for out-of-network
 * @returns The appropriate label or empty string for invalid values
 */
export const getNetworkLabel = (
  networkStatus: string | undefined,
  inNetworkLabel: string,
  outOfNetworkLabel: string
): string => {
  if (!networkStatus) return '';
  
  const normalized = networkStatus.toLowerCase();
  if (normalized === 'true') {
    return inNetworkLabel;
  } else if (normalized === 'false') {
    return outOfNetworkLabel;
  }
  return '';
};

export const formatCellValue = (
  key: keyof ServiceTotals,
  value: any
): string => {
  if (key === 'notes') {
    return value;
  }
  // If value is null, it means the field was not present in the API response
  if (value === null) {
    return '-';
  }
  if (typeof value === 'number') {
    return value === 0 ? '$0' : `$${formatAmount(value)}`;
  }
  return '-';
};

export const safeJsonParse = <T>(input: unknown, fallback: T): T => {
  if (typeof input !== 'string' || input.trim() === '') {
    return (input as T) ?? fallback;
  }
  try {
    const parsed = JSON.parse(input);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

export const generateHashedKey = async (
  baseKey: string,
  memberId: string
): Promise<string> => {
  const salt = 'healthplan_portal_v1';
  const encoded = new TextEncoder().encode(`${salt}_${memberId}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);

  const hashHex = Array.from(new Uint8Array(hashBuffer), (b) =>
    b.toString(16).padStart(2, '0')
  ).join('');

  return `${baseKey}_${hashHex}`;
};

export const formatAmount = (value: number | string): string => {
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  if (num === 0) return '0';
  
  const formatted = num.toFixed(2);
  // Strip trailing .00 for whole numbers
  return formatted.endsWith('.00') ? formatted.slice(0, -3) : formatted;
};

export const useExtension = (extensions: Extension[]) => {
  return useMemo(() => {
    const map: Record<string, string> = {};
    extensions.forEach((ext) => {
      if (ext.url) {
        map[ext.url.toLowerCase()] = ext.value ?? '';
      }
    });
    return map;
  }, [extensions]);
};

export const useIdentifier = (identifiers: Identifier[]) => {
  return useMemo(() => {
    const map: Record<string, string> = {};
    identifiers.forEach((identifier) => {
      if (identifier?.system) {
        map[identifier.system] = identifier.value ?? '';
      }
    });
    return map;
  }, [identifiers]);
};

const getServiceType = (type?: CodeableConcept): string => {
  let value = '';
  if (type?.coding) {
    if (Array.isArray(type.coding)) {
      value = type.coding[0]?.display ?? '';
    } else if (typeof type.coding === 'object') {
      value = (type.coding as { display?: string })?.display ?? '';
    }
  }
  if (value.includes('|')) {
    value = getUniqueParts(value).join(' | ');
  }
  return value;
};

const useServiceType = (type?: CodeableConcept): string => {
  return useMemo(() => getServiceType(type), [type]);
};

export const useMedicalClaimItem = (
  claimData: Claim,
  claimIndex: number
): MedicalClaimDataType => {
  const { getFeature } = useContext(GlobalContext);

  const {
    status: claimStatus,
    type,
    extension = [],
    created,
    total = [],
    identifier = [],
  } = claimData;

  const status = claimStatus
    ? getNormalizedStatus(String(claimStatus))
    : 'In Review';

  const extensionMap = useExtension(extension);
  const identifierMap = useIdentifier(identifier);

  const amountOwed = useMemo(() => {
    const patientTotal = total?.find((t) => {
      const codingValue = t?.category?.coding;
      const codings: any[] = Array.isArray(codingValue)
        ? codingValue
        : codingValue
        ? [codingValue]
        : [];

      return (
        codings.length > 0 &&
        codings.some((coding) => coding?.code === PATIENT_RESPONSIBILITY_CODE)
      );
    });

    return patientTotal?.amount?.value ? `$${patientTotal.amount.value}` : '$0';
  }, [total]);

  const practiceName = extensionMap[PRACTICE_NAME_URL.toLowerCase()] ?? '';
  const providerName = extensionMap[PROVIDER_NAME_URL.toLowerCase()] ?? '';
  const network = extensionMap[medicalClaimsConstants.inNetwork.toLowerCase()];
  const eobFlag = (extensionMap['eobflag'] ?? '') === 'True';
  const typeOfCare = useServiceType(type);

  let serviceDate = '';
  if ((claimData as any).normalizedServiceDate) {
    serviceDate = (claimData as any).normalizedServiceDate;
  } else {
    // Extract ISO date and format for display
    const isoDateString = extractISODateFromClaim(created);
    serviceDate = isoDateString ? formatYYYYMMDDToMDY(isoDateString) : '';
  }

  const showPayBill = showPayButton(
    claimStatus ? String(claimStatus) : undefined,
    getFeature?.PayYourSentaraBill
  );
  const claimId =
    Object.entries(identifierMap).find(([key]) =>
      key.includes('claimId')
    )?.[1] ?? '';
  const memberId =
    Object.entries(identifierMap).find(([key]) =>
      key.includes(medicalClaimsConstants.memberId)
    )?.[1] ?? '';

  return {
    id: `${claimIndex}`,
    index: claimIndex,
    amountOwed,
    practiceName,
    providerName,
    serviceDate,
    status,
    typeOfCare,
    showPayBill,
    claimId,
    network,
    memberId,
    eobFlag,
  };
};

export const useExtractClaimTotals = (claimResponse: Claim): ClaimTotals => {
  const { total = [], payment } = claimResponse;

  const totalsMap = useMemo(() => {
    if (!total.length) return {} as Record<string, string>;

    return total.reduce<Record<string, string>>((acc, item: Claim) => {
      const code = item.category?.coding?.code?.toLowerCase();
      if (!code) return acc;

      const value = item.amount?.value ?? '0';
      const parsedValue = Number.parseFloat(value);
      if (parsedValue === 0) {
        acc[code] = '0';
      } else {
        const decimalPart = parsedValue.toString().split('.')[1];
        acc[code] = decimalPart ? parsedValue.toFixed(2) : parsedValue.toString();
      }
      return acc;
    }, {});
  }, [total]);

  const getValue = (code: string): string =>
    totalsMap[code.toLowerCase()] ?? '0';

  const totalCopay = Number(getValue(medicalClaimsConstants.totalCopay));
  const totalCoinsurance = Number(
    getValue(medicalClaimsConstants.totalCoinsurance)
  );
  const copayPlusCoinsurance = totalCopay > 0 ? totalCopay : totalCoinsurance;
  let datePaymentSentToProvider = '';
  if (payment?.date && typeof payment.date === 'string') {
    datePaymentSentToProvider = formatYYYYMMDDToMDY(payment.date);
  }
  return {
    totalCharges: getValue(medicalClaimsConstants.totalCharges),
    totalNotCovered: getValue(medicalClaimsConstants.totalNotCovered),
    totalCovered: getValue(medicalClaimsConstants.totalCovered),
    totalDeductible: getValue(medicalClaimsConstants.totalDeductible),
    copayPlusCoinsurance,
    totalPaidByPlan: getValue(medicalClaimsConstants.totalPaidByPlan),
    patientResponsibility: getValue(
      medicalClaimsConstants.totalPatientResponsibility
    ),
    datePaymentSentToProvider: datePaymentSentToProvider ?? '',
  };
};

const codeMap: Record<string, keyof ServiceTotals> = {
  charged: 'charged',
  paidByPlan: 'paidByPlan',
  patientResponsibility: 'patientResponsibility',
  amountNotCovered: 'amountNotCovered',
  amountAllowed: 'amountAllowed',
  deductible: 'deductible',
  copay: 'copay',
  coinsurance: 'coinsurance',
};

const createEmptyServiceTotals = (): ServiceTotals => ({
  serviceType: '',
  charged: null,
  paidByPlan: null,
  patientResponsibility: null,
  amountNotCovered: null,
  amountAllowed: null,
  deductible: null,
  copay: null,
  coinsurance: null,
  copayCoinsurance: null,
  notes: '-',
  noteDisplay: undefined,
});

const normalizeCodingArray = (coding: any): any[] => {
  if (Array.isArray(coding)) {
    return coding;
  }
  if (coding) {
    return [coding];
  }
  return [];
};

const findServiceType = (adjudications: Adjudication[]): string => {
  for (const adj of adjudications) {
    if (!adj.category) {
      continue;
    }
    const codingArr = normalizeCodingArray(adj.category.coding);
    const serviceTypeCoding = codingArr.find(
      (coding) => coding?.code === medicalClaimsConstants.itemServiceType
    );
    if (serviceTypeCoding) {
      return serviceTypeCoding.display ?? '';
    }
  }
  return '';
};

const findNoteDetails = (
  adjudications: Adjudication[]
): { noteCode: string; noteDisplay?: string } => {
  for (const adj of adjudications) {
    if (!adj.category) {
      continue;
    }
    const codingArr = normalizeCodingArray(adj.category.coding);
    const noteCoding = codingArr.find((coding) => coding?.code === 'note');

    if (noteCoding && adj.reason?.coding) {
      const reasonCoding = Array.isArray(adj.reason.coding)
        ? adj.reason.coding[0]
        : adj.reason.coding;

      return {
        noteCode: reasonCoding?.code ?? '-',
        noteDisplay: reasonCoding?.display,
      };
    }
  }
  return { noteCode: '-', noteDisplay: undefined };
};

const parseAdjudicationValue = (rawValue: string | number): number => {
  if (typeof rawValue === 'string') {
    return Number.parseFloat(rawValue) || 0;
  }
  if (typeof rawValue === 'number') {
    return rawValue;
  }
  return 0;
};

const processAdjudication = (
  adj: Adjudication,
  totals: ServiceTotals
): void => {
  if (!adj.category) return;

  const codingArr = normalizeCodingArray(adj.category.coding);
  const code = codingArr[0]?.code ?? '';
  const key = codeMap[code];

  if (key && adj.amount?.value !== undefined) {
    const value = parseAdjudicationValue(adj.amount.value);
    (totals[key] as number) = value;
  }
};

const processAllAdjudications = (
  adjudications: Adjudication[],
  totals: ServiceTotals
): void => {
  adjudications.forEach((adj) => processAdjudication(adj, totals));
};

const createServiceTotal = (claimItem: any): ServiceTotals => {
  const adjudications: Adjudication[] = claimItem.adjudication ?? [];
  const { noteCode, noteDisplay } = findNoteDetails(adjudications);

  const totals = createEmptyServiceTotals();
  totals.serviceType = findServiceType(adjudications);
  totals.notes = noteCode;
  totals.noteDisplay = noteDisplay;

  processAllAdjudications(adjudications, totals);
  // Set copayCoinsurance: prefer copay if it has a non-null value, otherwise use coinsurance
  if (totals.copay !== null && totals.copay > 0) {
    totals.copayCoinsurance = totals.copay;
  } else if (totals.coinsurance !== null) {
    totals.copayCoinsurance = totals.coinsurance;
  }
  // else copayCoinsurance remains null (as initialized)

  return totals;
};

export const useExtractServiceTypeSummary = (
  claimData: Claim
): ServiceTotals[] => {
  return useMemo(() => {
    const items = claimData?.item;
    const itemsArray = Array.isArray(items) ? items : items ? [items] : [];

    if (!itemsArray.length) return [];

    return itemsArray.map(createServiceTotal);
  }, [claimData?.item]);
};
export const getTypeOfService = (claimData: Claim): string => {
  const coding = claimData?.type?.coding;
  if (!coding) return '';

  if (Array.isArray(coding)) return coding[0]?.display ?? '';
  if (typeof coding === 'object')
    return (coding as { display?: string })?.display ?? '';
  return '';
};

export const pdfConverter = (data: string, id: string) => {
  if (!data) return;
  const binary = atob(data.replaceAll(/([^\r])\n/g, '$1\r\n'));
  const len = binary.length;
  const buffer = new ArrayBuffer(len);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < len; i++) view[i] = binary.codePointAt(i) ?? 0;
  const blob = new Blob([view], { type: 'application/pdf' });
  saveAs(blob, id);
};

export const useRelatedClaim = (
  referenceClaim: Claim | null,
  list: Claim[],
  currentClaimId: string
): Claim[] => {
  const refIdentifierMap = useIdentifier(referenceClaim?.identifier ?? []);
  const refExtensionMap = useExtension(referenceClaim?.extension ?? []);

  const baseAuthId = refIdentifierMap['authorization-id'];
  const baseDxCode = refExtensionMap['diagnosiscode'];
  const baseFacility = refExtensionMap['practicename'];
  const baseDOS = referenceClaim?.created;

  const baseDxCodes = useMemo(
    () => (baseDxCode ? baseDxCode.split('|').filter(Boolean) : []),
    [baseDxCode]
  );

  return useMemo(() => {
    return list.filter((claim) => {
      if (!claim) return false;

      const claimIdentifierMap =
        claim.identifier?.reduce<Record<string, string>>((map, id) => {
          if (id?.system) map[id.system] = id.value ?? '';
          return map;
        }, {}) ?? {};

      const claimExtensionMap =
        claim.extension?.reduce<Record<string, string>>((map, ext) => {
          if (ext?.url) map[ext.url.toLowerCase()] = ext.value ?? '';
          return map;
        }, {}) ?? {};

      const id = claim.identifier?.find(
        (identifier) =>
          identifier?.system?.includes('claimId') && identifier.value
      )?.value;
      if (id === currentClaimId) return false;

      const authId = claimIdentifierMap['authorization-id'];
      const dxCode = claimExtensionMap['diagnosiscode'];
      const facility = claimExtensionMap['practicename'];
      const dos = claim?.created;

      if (baseAuthId && authId && baseAuthId === authId) return true;

      if (!dxCode || !facility || !dos) return false;

      const dxCodes = dxCode ? dxCode.split('|').filter(Boolean) : [];
      const dxMatch =
        baseDxCodes.length > 0 &&
        dxCodes.length > 0 &&
        baseDxCodes.some((code) => dxCodes.includes(code));

      const dosMatch = baseDOS && dos && dos === baseDOS;
      const facMatch = baseFacility && facility && baseFacility === facility;

      return dxMatch && dosMatch && facMatch;
    });
  }, [list, baseAuthId, baseDxCodes, baseDOS, baseFacility, currentClaimId]);
};

export function getClaimDateRange() {
  const today = new Date();
  const endDate = toISODateString(today);
  const startDateObj = new Date(today);
  startDateObj.setMonth(startDateObj.getMonth() - 18);
  const startDate = toISODateString(startDateObj);
  return { startDate, endDate };
}

/**
 * Determines if a custom date range is completely within the last 18 months.
 * @param customFromDate User-selected start date (YYYY-MM-DD)
 * @param customToDate User-selected end date (YYYY-MM-DD)
 * @returns true if range is within last 18 months, false otherwise
 */
export function isCustomDateWithin18Months(
  customFromDate: string,
  customToDate: string
): boolean {
  if (!customFromDate || !customToDate) return true; // No custom range = within cache

  const { startDate: eighteenMonthsAgoStr } = getClaimDateRange();
  const eighteenMonthsAgo = new Date(eighteenMonthsAgoStr);
  const userStart = new Date(customFromDate);

  // If user's start date is on or after the 18-month window start, it's within cache
  return userStart >= eighteenMonthsAgo;
}

/**
 * Gets the appropriate date range for API call based on user's custom selection.
 * If the user's range goes older than 18 months, we fetch from userStartDate to today.
 * Otherwise, we use the default 18-month range.
 * @param customFromDate User-selected start date (already normalized to YYYY-MM-DD)
 * @param customToDate User-selected end date (already normalized to YYYY-MM-DD)
 * @returns API request date range { startDate, endDate, shouldCallApi }
 */
export function getCustomDateRange(
  customFromDate?: string,
  customToDate?: string
): { startDate: string; endDate: string; shouldCallApi: boolean } {
  // No custom dates = use default 18-month range, no need to call API if cached
  if (!customFromDate || !customToDate) {
    const defaultRange = getClaimDateRange();
    return { ...defaultRange, shouldCallApi: false };
  }

  // Check if within 18 months
  const withinCache = isCustomDateWithin18Months(customFromDate, customToDate);

  if (withinCache) {
    // Range is within cached 18 months - no API call needed
    const defaultRange = getClaimDateRange();
    return { ...defaultRange, shouldCallApi: false };
  }

  const today = new Date();
  const endDate = toISODateString(today);
  return {
    startDate: customFromDate,
    endDate,
    shouldCallApi: true,
  };
}
